import { cn, convertCamelToServerError, insertObjectIf } from '@/utils';
import React, { memo, useMemo } from 'react';
import CurrencyInput from 'react-currency-input-field';

import { Input, InputProps } from 'antd';

import { Field, FieldProps } from 'formik';

export interface IFTextInput extends InputProps {
	name: string;
	inputClassName?: string;
	formatInput?: (value: string, number?: number) => void;
	currency?: boolean;
	inputRef?: any;
	hasServerError?: boolean;
	isUploadFileName?: boolean;
	fileExtension?: string;
}

const FTextInput: React.FC<IFTextInput> = ({
	name,
	inputClassName = '',
	formatInput,
	currency,
	inputRef,
	hasServerError,
	isUploadFileName,
	fileExtension,
	...props
}) => {
	return (
		<Field name={name}>
			{({
				field, // { name, value, onChange, onBlur }
				form: { setValues }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
				meta,
			}: FieldProps) => {
				const _value = useMemo(() => {
					if (isUploadFileName && fileExtension) {
						return field.value.split('.').slice(0, -1).join('.');
					}
					return field.value;
				}, [isUploadFileName, fileExtension, field.value]);

				const _onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
					const serverErrorKey = convertCamelToServerError(String(name));
					const value = e.target.value;
					let _value = value;

					if (isUploadFileName && fileExtension) {
						_value = `${value}.${fileExtension}`;
					}

					const formattedValue = formatInput
						? formatInput(_value, props?.maxLength)
						: _value;

					setValues((prevValues: any) => ({
						...prevValues,
						[name]: formattedValue,
						...insertObjectIf(Object.hasOwn(prevValues, serverErrorKey), {
							[serverErrorKey]: false,
						}),
					}));
				};

				const transformRawValue = (rawValue: string) => {
					if (formatInput) {
						return formatInput(rawValue, props?.maxLength) as unknown as string;
					}
					return rawValue;
				};

				const _onValueChange = (value: string | undefined = '') => {
					if (props.maxLength && value?.length > props.maxLength) {
						return;
					}

					const serverErrorKey = convertCamelToServerError(String(name));

					setValues((prevValues: any) => ({
						...prevValues,
						[name]: value,
						...insertObjectIf(Object.hasOwn(prevValues, serverErrorKey), {
							[serverErrorKey]: false,
						}),
					}));
				};

				return (
					<div className="flex-1">
						{currency ? (
							<CurrencyInput
								ref={inputRef}
								name={name}
								decimalsLimit={2}
								onValueChange={_onValueChange}
								className={cn(
									'bg-white w-full text-[13px] text-font2 h-[11.111vw] xl:h-[30px] border border-gray1 px-[11px] py-[7px] focus:outline-none focus:border-blue2 ',
									inputClassName,
									meta.touched && meta.error && '!border-red7',
									hasServerError && '!border-red7'
								)}
								defaultValue={field.value}
								value={field.value}
								allowNegativeValue={false}
								decimalSeparator="."
								groupSeparator=","
								intlConfig={{ locale: 'ko-KR' }}
								onBlur={(e) => {
									field?.onBlur?.(e);
									props?.onBlur?.(e);
								}}
								onKeyDown={props?.onKeyDown}
								transformRawValue={transformRawValue}
							/>
						) : (
							<Input
								ref={inputRef}
								{...field}
								value={_value}
								type="text"
								size="large"
								className={cn(
									'bg-white w-full text-[13px] text-font2 h-[11.111vw] xl:h-[30px] rounded-none',
									inputClassName,
									meta.touched && meta.error && '!border-red7',
									hasServerError && '!border-red7'
								)}
								onChange={_onChange}
								autoComplete="new-password"
								{...props}
							/>
						)}
					</div>
				);
			}}
		</Field>
	);
};

export default memo(FTextInput);
